import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ImmerGet, ImmerSet } from "..";
import { createContext, useContext } from "react";
import { EditorState } from "./editor-state";
import { EditorService } from "./editor-service";

export type Set = ImmerSet<EditorState>;
export type Get = ImmerGet<EditorState>;
export type EditorStore = ReturnType<typeof createEditorStore>;

export const createEditorStore = () => {
  return createStore<EditorState>()(
    immer<EditorState>((set, get) => ({
      videoExternalId: "",
      teamExternalId: "",
      isInitialized: false,
      video: null,
      isEditingVideoName: false,
      sourceImages: {},
      animatedImages: {},
      voiceOvers: [],
      backgroundMusics: {},
      thirdPartyEditorInfo: null,
      previewVoiceOver: null,
      isPreviewVoiceOverPlaying: false,
      voiceOverForm: {
        script: "",
        voiceActorExternalId: null,
      },
      voiceActors: [],
      compositionFonts: [],
      assetMedias: {},
      creativeElements: {},
      animationForm: {
        items: {},
        provider: null,
      },
      cropperState: null,
      creditDialogState: {
        isOpen: false,
        openedFromAnimate: false,
      },
      sourceImageSelectorDialogState: {
        isOpen: false,
      },
      quickAnimationsDialogState: {
        openTimelineItemExternalId: null,
      },
      backgroundMusicDialogState: {
        view: "HIDDEN",
        editingTrackExternalId: undefined,
      },
      fontDialogState: {
        view: "HIDDEN",
      },
      editorPanelState: {
        currentTab: "images",
      },
      voiceoverTabState: {
        view: "CREATE_VOICEOVER",
        scriptWriterInput: "",
      },
      overlaysTabState: {
        currentTab: "elements",
        currentElement: null,
        mediaSelectorDialogState: {
          isOpen: false,
        },
      },
      videoPreviewState: {
        isPlaying: false,
        isFullscreen: false,
        isMuted: false,
      },
      creativeProjectSheetState: {
        currentCreativeProjectExternalId: null,
        isSheetOpen: false,
        creativeProjects: {},
      },
      service: new EditorService(set, get),
    })),
  );
};

export const EditorStoreContext = createContext<ReturnType<
  typeof createEditorStore
> | null>(null);

export function useEditorStore<U>(selector: (state: EditorState) => U) {
  const store = useContext(EditorStoreContext);

  if (store === null) {
    throw new Error("useEditorStore must be used within EditorStoreProvider");
  }

  return useStore(store, selector);
}
